import { useState, useCallback } from "react"
import {
  Modal,
  ModalBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Form,
  Card,
  CardBody,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { useEditShowroom } from "hooks/showroomHook"
import ButtonTooltip from "components/Common/ButtonTooltip"
import coverPlaceholder from "assets/images/profile-img.png"
import profilePlaceholder from "assets/images/users/avatar-1.jpg"

export default function ShowroomProfileUpdate({ showroom = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { mutateAsync: editShowroom, isPending: showroomEditLoading } =
    useEditShowroom()

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    short_name: Yup.string().nullable(),
    address: Yup.string().nullable(),
    proprietor_name: Yup.string().nullable(),
    proprietor_mobile: Yup.string().nullable(),
    proprietor_address: Yup.string().nullable(),
    cover_image: Yup.string().nullable(),
    logo: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async values => {
      try {
        const res = await editShowroom({ id: showroom?.id, ...values })
        toggle()
        toast.success(res?.message ?? "Showroom profile updated successfully.")
      } catch (error) {
        console.error("Failed to update showroom profile:", error)
        toast.error("Failed to update showroom profile.")
      }
    },
    [editShowroom, showroom]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: showroom?.name || "",
      short_name: showroom?.short_name || "",
      address: showroom?.address || "",
      proprietor_name: showroom?.proprietor_name || "",
      proprietor_mobile: showroom?.proprietor_mobile || "",
      proprietor_address: showroom?.proprietor_address || "",
      cover_image: showroom?.cover_image || "",
      logo: showroom?.logo || "",
    },
    validationSchema,
    onSubmit,
  })

  return (
    <>
      {/* Edit button */}
      <div className="d-flex justify-content-center mt-2">
        <ButtonTooltip
          type="edit"
          onClick={toggle}
          btnId="edit-showroom-list"
          btnName="Edit Showroom Profile"
        />
      </div>

      {/* Modal */}
      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <Card className="overflow-hidden mb-0">
          <div
            className="profile-cover position-relative"
            style={{
              backgroundImage: `url(${validation.values.cover_image || coverPlaceholder})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "150px",
            }}
          >
            <div
              className="d-flex justify-content-center align-items-end"
              style={{ height: "100%" }}
            >
              <img
                src={validation.values.logo || profilePlaceholder}
                alt="Showroom Logo"
                className="rounded-circle img-thumbnail"
                style={{
                  width: "100px",
                  height: "100px",
                  marginBottom: "-50px",
                  border: "3px solid #fff",
                  backgroundColor: "#fff",
                }}
              />
            </div>
          </div>
          <CardBody className="text-center mt-5 pt-4">
            <h5 className="font-size-15">{validation.values.name}</h5>
            <p className="text-muted mb-0">{validation.values.short_name}</p>
            <p className="text-muted mb-0">{validation.values.address}</p>
          </CardBody>
        </Card>

        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col md={6}>
                <Label>Name *</Label>
                <Input
                  name="name"
                  value={validation.values.name}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.name}
                />
              </Col>
              <Col md={6}>
                <Label>Short Name</Label>
                <Input
                  name="short_name"
                  value={validation.values.short_name}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.short_name}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Address</Label>
                <Input
                  name="address"
                  type="textarea"
                  value={validation.values.address}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.address}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>Proprietor Name</Label>
                <Input
                  name="proprietor_name"
                  value={validation.values.proprietor_name}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.proprietor_name}
                />
              </Col>
              <Col md={6}>
                <Label>Proprietor Mobile</Label>
                <Input
                  name="proprietor_mobile"
                  value={validation.values.proprietor_mobile}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.proprietor_mobile}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Proprietor Address</Label>
                <Input
                  name="proprietor_address"
                  type="textarea"
                  value={validation.values.proprietor_address}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.proprietor_address}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>Cover Image URL</Label>
                <Input
                  name="cover_image"
                  value={validation.values.cover_image}
                  onChange={validation.handleChange}
                />
              </Col>
              <Col md={6}>
                <Label>Logo URL</Label>
                <Input
                  name="logo"
                  value={validation.values.logo}
                  onChange={validation.handleChange}
                />
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button type="submit" color="success" disabled={showroomEditLoading}>
                Save Changes {showroomEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
